/**
 * components/ui/SaveConfirmation.tsx
 * ------------------------------------------------------------------
 * Full-screen overlay confirmation modal for save operations.
 * Dims the entire screen and shows a prominent success/error message
 * in the center. Auto-dismisses after a few seconds.
 *
 * Also exports useAutoSave hook for forms that need auto-save.
 * ------------------------------------------------------------------
 */
import { useEffect, useCallback, useRef } from 'react';
import { CheckCircle2, XCircle, Loader2, Save } from 'lucide-react';

/* ==================================================================
   STICKY SAVE BAR — always-visible save affordance for long forms
   ==================================================================
   A bottom-pinned "unsaved changes · Save" pill that stays in view as
   the user scrolls a long form, so Save is always one click away and the
   dirty state is obvious. Render it near the end of a surface; it only
   shows when `visible` (usually the form's `dirty` flag) is true. */
export function StickySaveBar({
  visible,
  onSave,
  saving = false,
  disabled = false,
  message = 'Unsaved changes',
  saveLabel = 'Save',
}: {
  visible: boolean;
  onSave: () => void;
  saving?: boolean;
  disabled?: boolean;
  message?: string;
  saveLabel?: string;
}) {
  if (!visible) return null;
  return (
    <div className="sticky bottom-4 z-40 mt-4 flex justify-center pointer-events-none">
      <div className="pointer-events-auto flex items-center gap-3 pl-4 pr-2 py-2 rounded-2xl bg-white/95 dark:bg-gray-800/95 backdrop-blur border border-gray-200 dark:border-gray-700 shadow-xl">
        <span className="flex items-center gap-1.5 text-sm font-medium text-amber-600 dark:text-amber-400">
          <span className="w-2 h-2 rounded-full bg-amber-400 animate-pulse" />
          {message}
        </span>
        <button
          type="button"
          onClick={onSave}
          disabled={disabled || saving}
          className="flex items-center gap-2 px-5 py-2 rounded-xl bg-gradient-to-r from-[#2d5295] to-[#1b3a6b] text-white text-sm font-semibold shadow-md hover:brightness-110 disabled:opacity-50 transition"
        >
          {saving ? <Loader2 size={15} className="animate-spin" /> : <Save size={15} />}
          {saving ? 'Saving…' : saveLabel}
        </button>
      </div>
    </div>
  );
}

/* ==================================================================
   SAVE CONFIRMATION MODAL
   ================================================================== */

interface SaveConfirmationProps {
  show: boolean;
  status: 'saving' | 'success' | 'error';
  message: string;
  onDismiss: () => void;
  /** Auto-dismiss after this many ms (default 2500). Set 0 to disable. */
  autoDismissMs?: number;
}

export function SaveConfirmation({
  show,
  status,
  message,
  onDismiss,
  autoDismissMs = 2500,
}: SaveConfirmationProps) {
  useEffect(() => {
    if (show && status !== 'saving' && autoDismissMs > 0) {
      const t = setTimeout(onDismiss, autoDismissMs);
      return () => clearTimeout(t);
    }
  }, [show, status, autoDismissMs, onDismiss]);

  if (!show) return null;

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center bg-black/50 backdrop-blur-sm animate-fade-in"
      onClick={status !== 'saving' ? onDismiss : undefined}
    >
      <div
        className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl p-8 max-w-sm w-full mx-4 text-center animate-scale-in"
        onClick={(e) => e.stopPropagation()}
      >
        {status === 'saving' && (
          <>
            <Loader2 size={48} className="mx-auto text-primary animate-spin mb-4" />
            <p className="text-lg font-semibold text-gray-800 dark:text-white">Saving…</p>
            <p className="text-sm text-gray-500 mt-1">{message || 'Please wait'}</p>
          </>
        )}
        {status === 'success' && (
          <>
            <div className="w-16 h-16 rounded-full bg-emerald-100 dark:bg-emerald-900/30 flex items-center justify-center mx-auto mb-4">
              <CheckCircle2 size={36} className="text-emerald-500" />
            </div>
            <p className="text-lg font-semibold text-gray-800 dark:text-white">Saved!</p>
            <p className="text-sm text-gray-500 mt-1">{message}</p>
          </>
        )}
        {status === 'error' && (
          <>
            <div className="w-16 h-16 rounded-full bg-red-100 dark:bg-red-900/30 flex items-center justify-center mx-auto mb-4">
              <XCircle size={36} className="text-red-500" />
            </div>
            <p className="text-lg font-semibold text-gray-800 dark:text-white">Error</p>
            <p className="text-sm text-red-600 dark:text-red-400 mt-1">{message}</p>
            <button
              onClick={onDismiss}
              className="mt-4 px-4 py-2 bg-gray-100 dark:bg-gray-700 rounded-lg text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-200 transition-colors"
            >
              Dismiss
            </button>
          </>
        )}
      </div>
    </div>
  );
}

/* ==================================================================
   AUTO-SAVE HOOK
   ================================================================== */

/**
 * useAutoSave — triggers a save callback after a period of inactivity.
 * Resets the timer on every call to `touch()`.
 *
 * @param saveFn  The async save function to call
 * @param delayMs How long to wait after last change before auto-saving (default 5 min)
 * @param enabled Whether auto-save is active
 */
export function useAutoSave(
  saveFn: () => Promise<void>,
  delayMs = 300000,
  enabled = true,
) {
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const saveFnRef = useRef(saveFn);
  saveFnRef.current = saveFn;

  const touch = useCallback(() => {
    if (!enabled) return;
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => {
      saveFnRef.current().catch(console.error);
    }, delayMs);
  }, [delayMs, enabled]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, []);

  // Also auto-save on page hide (user navigates away or closes tab)
  useEffect(() => {
    if (!enabled) return;
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'hidden' && timerRef.current) {
        clearTimeout(timerRef.current);
        saveFnRef.current().catch(console.error);
      }
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, [enabled]);

  return { touch };
}
